import React, { Component } from "react";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { Row, Col, Form, FormGroup, Alert } from "react-bootstrap";
import _ from "lodash";
import moment from "moment";

import Button from "../../../../components/common/button/Button";
import DatePicker2 from "../../../../components/common/datepicker/DatePicker2";
import Input from "../../../../components/common/input/Input";
import Select2 from "../../../../components/common/select/Select2";
import {
  required,
  date,
  dropdown,
  minNumber,
  comma,
  floatWithComma
} from "../../../../utils/forms/validators";
import Steps from "../../../../components/common/steps/Steps";
import ScrollToTopOnMount from "../../../../utils/scroll/ScrollToTopOnMount";

import { getProject } from "../../../../actions/project.actions";

import styles from "./ProjectBudget.scss";

const rateFloat = floatWithComma("Please enter a valid number");
const rateComma = comma("Please enter numeric characters only");
const rateMin = minNumber(50, "Please enter a valid number");
const dateVal = date("Please enter a valid Date.");
const rateTypeVal = dropdown("Please select a Rate Type.");

const RATE_TYPE_UNSELECTED = -1;
const RATE_TYPE_HOURLY = 74;
const RATE_TYPE_DAILY = 75;
const RATE_TYPE_WEEKLY = 76;
const RATE_TYPE_MONTHLY = 77;
const RATE_TYPE_NEGOTIABLE = 78;

const validate = values => {
  const errors = {};

  if (
    values.budget_run_start &&
    values.budget_run_end &&
    moment(values.budget_run_end).isBefore(values.budget_run_start)
  ) {
    errors.budget_run_end = `The end date should be after the start date`;
  }

  return errors;
};

class ProjectBudget extends Component {
  constructor(props) {
    super(props);

    this.state = {
      rate: null,
      startDate: null
    };

    this.handleRateChange = this.handleRateChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
  }

  componentDidMount() {
    this.handleStartDateChange(moment());
  }

  handleSelect() {
    this.props.change("budget_rate_value", null);
    this.props.change("budget_rate_period", null);
  }

  handleRateChange(event) {
    const value = _.toNumber(event.target.value);
    this.setState({ rate: value });
  }

  handleStartDateChange(value) {
    this.setState({ startDate: moment(value) });
  }

  renderRateField() {
    // Negotiable
    if (
      !this.props.rateType ||
      this.props.rateType == RATE_TYPE_UNSELECTED ||
      this.props.rateType == RATE_TYPE_NEGOTIABLE
    )
      return null;

    // Other
    return (
      <Field
        name="budget_rate_value"
        type="text"
        placeholder="$ Rate"
        component={Input}
        onChange={this.handleRateChange}
        validate={[required, rateComma, rateFloat, rateMin]}
      />
    );
  }

  renderRatePeriodField() {
    if (!this.props.rateType || this.props.rateType == RATE_TYPE_UNSELECTED)
      return null;

    // Hourly || Daily
    if (
      this.props.rateType == RATE_TYPE_HOURLY ||
      this.props.rateType == RATE_TYPE_DAILY ||
      this.props.rateType == RATE_TYPE_WEEKLY ||
      this.props.rateType == RATE_TYPE_MONTHLY
    ) {
      const placeholder =
        this.props.rateType == RATE_TYPE_DAILY
          ? "Est. Days/Week"
          : "Est. Hours/Week";
      /*let type = 'd/w';
      if (this.props.rateType == RATE_TYPE_HOURLY ||
        this.props.rateType == RATE_TYPE_WEEKLY ||
        this.props.rateType == RATE_TYPE_MONTHLY ) type = 'h/w';*/
      return (
        <Field
          formGroupClassName={
            this.props.rateType == RATE_TYPE_HOURLY && styles.hoursInput
          }
          name="budget_rate_period"
          type="text"
          placeholder={placeholder}
          component={Input}
          //validate={ type === 'h/w' ? [required, hours] : [required, week, numericWeek]}
        />
      );
    }
    return null;
  }

  render() {
    const { submitting, valid, projectBudgetRateTypes } = this.props;

    const budgetRateTypes = _.filter(
      projectBudgetRateTypes,
      item => item.value !== "Negotiable"
    );

    return (
      <section className={styles.projectBudget}>
        <ScrollToTopOnMount />
        <div className={styles.header}>
          <Steps steps={this.props.steps} activeStep={3} track={true} />
        </div>
        <div className={styles.content}>
          <h2 className={styles.title}>Budgeting</h2>

          <Form onSubmit={this.props.handleSubmit}>
            <div className={styles.form}>
              <FormGroup>
                <div className={styles.subtitle}>
                  Help Members decide how best to pitch for your project by
                  providing budget guidance below.
                </div>
              </FormGroup>
              <FormGroup>
                <div className={styles.dividerDouble} />

                <div className={styles.sectionTitle}>Budget</div>

                <Row>
                  <Col md={4} xs={12}>
                    <Field
                      name="budget_rate_type_id"
                      type="select"
                      placeholder="Rate Type"
                      component={Select2}
                      options={budgetRateTypes}
                      onSelectChange={this.handleSelect}
                      validate={[required, rateTypeVal]}
                    />
                  </Col>
                  <Col md={4} xs={12}>
                    {this.renderRateField()}
                  </Col>
                  <Col md={4} xs={12}>
                    {this.renderRatePeriodField()}
                  </Col>
                </Row>

                {(this.state.rate && this.state.rate < 50) ||
                this.state.rate === 0 ? (
                  <Row>
                    <br />
                    <Alert bsStyle="danger" className={styles.budgetError}>
                      Our members typically charge a minimum of $50/hour. We
                      suggest you enter a higher rate, select a project rate or
                      choose "Negotiable" to proceed.
                    </Alert>
                  </Row>
                ) : null}

                <div className={styles.divider} />

                <div className={styles.sectionTitle}>
                  Project Run (Optional)
                </div>

                <Row>
                  <Col md={4} xs={12}>
                    <Field
                      name="budget_run_start"
                      placeholder="Start"
                      component={DatePicker2}
                      minDate={moment()}
                      onSelectDate={this.handleStartDateChange}
                      validate={dateVal}
                    />
                  </Col>
                  <Col md={4} xs={12}>
                    <Field
                      name="budget_run_end"
                      placeholder="End"
                      component={DatePicker2}
                      minDate={this.state.startDate || moment()}
                      validate={dateVal}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <div className={styles.dividerDouble} />

              <div className={styles.CTAContainer}>
                <Button
                  isPrimary
                  type="submit"
                  size={"large"}
                  label="Save + Continue"
                  isDisabled={!valid || submitting}
                />
              </div>
            </div>
          </Form>
        </div>
      </section>
    );
  }
}

ProjectBudget = reduxForm({
  form: "projectBudget",
  enableReinitialize: true,
  validate
})(ProjectBudget);

const selector = formValueSelector("projectBudget");
ProjectBudget = connect(
  state => {
    const rateType = selector(state, "budget_rate_type_id");
    return {
      rateType,
      initialValues: state.project.data.data
    };
  },
  dispatch => {
    return {
      getProject: () => dispatch(getProject())
    };
  }
)(ProjectBudget);

export default ProjectBudget;



// WEBPACK FOOTER //
// ./src/routes/employer/project/components/ProjectBudget.js